import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { getProducts, createProduct, updateProduct, deleteProduct } from '../../services/productService';
import { getBrands } from '../../services/brandService';
import { getCategories } from '../../services/categoryService';
import {
  getProductImages,
  createProductImage,
  updateProductImage,
  deleteProductImage,
} from '../../services/productImageService';
import { uploadFile } from '../../services/upload';
import './Products.css';

const PRODUCT_STATUS = {
  DELETED: 0,
  INACTIVE: 100,
  ACTIVE: 200,
};

const statusOptions = [
  { value: PRODUCT_STATUS.ACTIVE, label: 'Active' },
  { value: PRODUCT_STATUS.INACTIVE, label: 'Inactive' },
  { value: PRODUCT_STATUS.DELETED, label: 'Deleted' },
];

const getStatusMeta = (value) => {
  const num = Number(value);
  if (num === PRODUCT_STATUS.ACTIVE) return { label: 'Active', tone: 'success' };
  if (num === PRODUCT_STATUS.INACTIVE) return { label: 'Inactive', tone: 'warning' };
  return { label: 'Deleted', tone: 'muted' };
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [activeTab, setActiveTab] = useState('uz');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  const [search, setSearch] = useState('');
  const [brandFilterGuid, setBrandFilterGuid] = useState('');
  const [categoryFilterGuid, setCategoryFilterGuid] = useState('');
  const [popularFilter, setPopularFilter] = useState('');
  const [onSaleFilter, setOnSaleFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const [productImages, setProductImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [deleteImageModalOpen, setDeleteImageModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);

  const imageInputRef = useRef(null);
  const quillRef = useRef(null);

  const [formData, setFormData] = useState({
    name: { uz: '', ru: '', oz: '' },
    description: { uz: '', ru: '', oz: '' },
    price: '',
    discount: '0',
    percent: '0',
    is_popular: false,
    on_sale: false,
    brand_guid: '',
    category_guid: '',
    status: PRODUCT_STATUS.ACTIVE,
  });

  const [imageType, setImageType] = useState(1);

  const brandMap = useMemo(() => {
    const map = new Map();
    (brands || []).forEach((b) => map.set(b.guid, b));
    return map;
  }, [brands]);

  const categoryMap = useMemo(() => {
    const map = new Map();
    (categories || []).forEach((c) => map.set(c.guid, c));
    return map;
  }, [categories]);

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        try {
          const uploadResp = await uploadFile(file);
          const url = uploadResp?.data?.link;
          if (url && quillRef.current) {
            const quill = quillRef.current.getEditor();
            const range = quill.getSelection(true);
            quill.insertEmbed(range.index, 'image', url);
          }
        } catch (error) {
          console.error('Error uploading image to quill:', error);
        }
      }
    };
  }, []);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  }), [imageHandler]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * limit;
      const response = await getProducts({
        limit,
        offset,
        search,
        brand_guid: brandFilterGuid || undefined,
        category_guid: categoryFilterGuid || undefined,
        is_popular: popularFilter || undefined,
        on_sale: onSaleFilter || undefined,
      });
      setProducts(response.data.products || []);
      setTotal(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, brandFilterGuid, categoryFilterGuid, popularFilter, onSaleFilter]);

  const fetchBrandsAndCategories = useCallback(async () => {
    try {
      const [brandsResp, categoriesResp] = await Promise.all([
        getBrands({ limit: 1000, offset: 0, search: '' }),
        getCategories({ limit: 1000, offset: 0, search: '' }),
      ]);
      setBrands(brandsResp.data.brands || []);
      setCategories(categoriesResp.data.categories || []);
    } catch (error) {
      console.error('Error fetching lookup data:', error);
    }
  }, []);

  const fetchImages = useCallback(async (productGuid) => {
    if (!productGuid) return;
    setImagesLoading(true);
    try {
      const response = await getProductImages({ limit: 50, offset: 0, product_guid: productGuid });
      setProductImages(response.data.product_images || []);
    } catch (error) {
      console.error('Error fetching product images:', error);
    } finally {
      setImagesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrandsAndCategories();
  }, [fetchBrandsAndCategories]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleBrandFilterChange = (e) => {
    setBrandFilterGuid(e.target.value);
    setPage(1);
  };

  const handleCategoryFilterChange = (e) => {
    setCategoryFilterGuid(e.target.value);
    setPage(1);
  };

  const handlePopularFilterChange = (e) => {
    setPopularFilter(e.target.value);
    setPage(1);
  };

  const handleOnSaleFilterChange = (e) => {
    setOnSaleFilter(e.target.value);
    setPage(1);
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (brandFilterGuid) count += 1;
    if (categoryFilterGuid) count += 1;
    if (popularFilter) count += 1;
    if (onSaleFilter) count += 1;
    return count;
  }, [brandFilterGuid, categoryFilterGuid, popularFilter, onSaleFilter]);

  const showingFrom = total === 0 ? 0 : (page - 1) * limit + 1;
  const showingTo = total === 0 ? 0 : Math.min(page * limit, total);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleInputChange = (field, lang, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [lang]: value,
      },
    }));
  };

  const openModal = (product = null) => {
    setSaveStatus(null);
    setActiveTab('uz');
    setImageType(1);

    if (product) {
      setCurrentProduct(product);
      setFormData({
        name: product.name || { uz: '', ru: '', oz: '' },
        description: product.description || { uz: '', ru: '', oz: '' },
        price: typeof product.price === 'number' ? String(product.price) : (product.price || ''),
        discount: typeof product.discount === 'number' ? String(product.discount) : String(product.discount ?? 0),
        percent: typeof product.percent === 'number' ? String(product.percent) : String(product.percent ?? 0),
        is_popular: Boolean(product.is_popular),
        on_sale: Boolean(product.on_sale),
        brand_guid: product.brand_guid || product?.brand?.guid || '',
        category_guid: product.category_guid || product?.category?.guid || '',
        status: typeof product.status === 'number' ? product.status : PRODUCT_STATUS.ACTIVE,
      });
      setProductImages([]);
      fetchImages(product.guid);
    } else {
      setCurrentProduct(null);
      setFormData({
        name: { uz: '', ru: '', oz: '' },
        description: { uz: '', ru: '', oz: '' },
        price: '',
        discount: '0',
        percent: '0',
        is_popular: false,
        on_sale: false,
        brand_guid: '',
        category_guid: '',
        status: PRODUCT_STATUS.ACTIVE,
      });
      setProductImages([]);
    }

    setModalOpen(true);
  };

  const confirmDelete = (product) => {
    setProductToDelete(product);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!productToDelete) return;
    try {
      await deleteProduct(productToDelete.guid);
      await fetchProducts();
      setDeleteModalOpen(false);
      setProductToDelete(null);
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const buildProductPayload = () => {
    const priceNum = Number(formData.price);
    const discountNum = Number(formData.discount);
    const percentNum = Number(formData.percent);
    return {
      name: formData.name,
      description: formData.description,
      price: Number.isFinite(priceNum) ? priceNum : 0,
      discount: Number.isFinite(discountNum) ? Math.trunc(discountNum) : 0,
      percent: Number.isFinite(percentNum) ? Math.trunc(percentNum) : 0,
      is_popular: Boolean(formData.is_popular),
      on_sale: Boolean(formData.on_sale),
      brand_guid: formData.brand_guid || undefined,
      category_guid: formData.category_guid || undefined,
      status: Number(formData.status),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveStatus({ type: 'info', message: 'Saving…' });
    try {
      if (currentProduct) {
        const response = await updateProduct({
          ...buildProductPayload(),
          guid: currentProduct.guid,
        });
        setCurrentProduct(response.data);
        setSaveStatus({ type: 'success', message: 'Saved.' });
        await fetchProducts();
      } else {
        const response = await createProduct(buildProductPayload());
        setCurrentProduct(response.data);
        setSaveStatus({ type: 'success', message: 'Saved. You can now add images.' });
        await fetchProducts();
        await fetchImages(response.data.guid);
      }
    } catch (error) {
      console.error('Error saving product:', error);

      const apiMessage =
        error?.response?.data?.description ||
        error?.response?.data?.error ||
        (typeof error?.response?.data?.data === 'string' ? error.response.data.data : '') ||
        error?.message ||
        'Failed to save.';

      setSaveStatus({
        type: 'error',
        message: apiMessage,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !currentProduct?.guid) return;

    setImageUploading(true);
    try {
      const uploadResp = await uploadFile(file);
      const url = uploadResp?.data?.link;
      if (!url) {
        throw new Error('Upload response missing link');
      }

      await createProductImage({
        product_guid: currentProduct.guid,
        url,
        image_type: Number(imageType) || 1,
      });

      await fetchImages(currentProduct.guid);
    } catch (error) {
      console.error('Error uploading/creating product image:', error);
    } finally {
      setImageUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const toggleImageStatus = async (image) => {
    if (!currentProduct?.guid || !image?.guid) return;
    const current = Number(image.status);
    const next = current === PRODUCT_STATUS.ACTIVE ? PRODUCT_STATUS.INACTIVE : PRODUCT_STATUS.ACTIVE;

    try {
      await updateProductImage({
        guid: image.guid,
        product_guid: currentProduct.guid,
        url: image.url,
        status: next,
        image_type: typeof image.image_type === 'number' ? image.image_type : 1,
      });
      await fetchImages(currentProduct.guid);
    } catch (error) {
      console.error('Error updating product image:', error);
    }
  };

  const confirmDeleteImage = (img) => {
    setImageToDelete(img);
    setDeleteImageModalOpen(true);
  };

  const handleDeleteImage = async () => {
    if (!imageToDelete?.guid || !currentProduct?.guid) return;
    try {
      await deleteProductImage(imageToDelete.guid);
      await fetchImages(currentProduct.guid);
      setDeleteImageModalOpen(false);
      setImageToDelete(null);
    } catch (error) {
      console.error('Error deleting product image:', error);
    }
  };

  return (
    <div className="products-page">
      <div className="page-header">
        <div className="header-left">
          <div className="search-box">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={handleSearch}
            />
          </div>

          <div className="filters">
            <button
              type="button"
              className="filter-btn"
              onClick={() => setFiltersOpen((v) => !v)}
              aria-label="Open filters"
              aria-expanded={filtersOpen}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="7" y1="12" x2="17" y2="12" />
                <line x1="10" y1="18" x2="14" y2="18" />
              </svg>
              <span>Filter</span>
              {activeFilterCount > 0 ? <span className="filter-badge">{activeFilterCount}</span> : null}
            </button>

            {filtersOpen ? (
              <div className="filters-panel" role="region" aria-label="Filters">
                <div className="filter-row">
                  <label>Brand</label>
                  <select className="filter-select" value={brandFilterGuid} onChange={handleBrandFilterChange}>
                    <option value="">All brands</option>
                    {brands.map((b) => (
                      <option key={b.guid} value={b.guid}>
                        {b?.name?.uz || b.guid}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-row">
                  <label>Category</label>
                  <select className="filter-select" value={categoryFilterGuid} onChange={handleCategoryFilterChange}>
                    <option value="">All categories</option>
                    {categories.map((c) => (
                      <option key={c.guid} value={c.guid}>
                        {c?.name?.uz || c.guid}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-row">
                  <label>Popular</label>
                  <select className="filter-select" value={popularFilter} onChange={handlePopularFilterChange}>
                    <option value="">All</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>

                <div className="filter-row">
                  <label>On sale</label>
                  <select className="filter-select" value={onSaleFilter} onChange={handleOnSaleFilterChange}>
                    <option value="">All</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>

                <div className="filter-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setBrandFilterGuid('');
                      setCategoryFilterGuid('');
                      setPopularFilter('');
                      setOnSaleFilter('');
                      setPage(1);
                    }}
                    disabled={!brandFilterGuid && !categoryFilterGuid && !popularFilter && !onSaleFilter}
                  >
                    Clear
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setFiltersOpen(false)}>
                    Close
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="header-actions">
          <button className="add-btn" onClick={() => openModal()}>
            + Add Product
          </button>
        </div>
      </div>

      <div className="products-table-container">
        <table className="products-table">
          <thead>
            <tr>
              <th>№</th>
              <th>Image</th>
              <th>Name (UZ)</th>
              <th>Price</th>
              <th>Discount</th>
              <th>Percent</th>
              <th>Popular</th>
              <th>On sale</th>
              <th>Brand</th>
              <th>Category</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={12} className="table-empty">
                  Loading...
                </td>
              </tr>
            ) : products.length === 0 ? (
              <tr>
                <td colSpan={12} className="table-empty">
                  No products found.
                </td>
              </tr>
            ) : (
              products.map((p, index) => {
                const statusMeta = getStatusMeta(p.status);
                const brand = p.brand || brandMap.get(p.brand_guid);
                const category = p.category || categoryMap.get(p.category_guid);
                
                const imagesList = Array.isArray(p.product_images) ? p.product_images : (Array.isArray(p.images) ? p.images : []);
                const firstImage = imagesList[0]?.url || imagesList[0]?.image_url || (typeof imagesList[0] === 'string' ? imagesList[0] : null);
                const imagesCount = imagesList.length;

                return (
                  <tr key={p.guid}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {firstImage ? (
                          <img src={firstImage} alt="Product" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: '4px' }} />
                        ) : (
                          <div style={{ width: 40, height: 40, backgroundColor: 'var(--bg-tertiary)', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 10 }}>No IMG</div>
                        )}
                        {imagesCount > 0 && <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>({imagesCount})</span>}
                      </div>
                    </td>
                    <td>{p?.name?.uz || '-'}</td>
                    <td>{typeof p.price === 'number' ? p.price.toLocaleString() : p.price}</td>
                    <td>{typeof p.discount === 'number' ? p.discount : (p.discount ?? 0)}</td>
                    <td>{typeof p.percent === 'number' ? p.percent : (p.percent ?? 0)}</td>
                    <td>{p.is_popular ? 'Yes' : 'No'}</td>
                    <td>{p.on_sale ? 'Yes' : 'No'}</td>
                    <td>{brand?.name?.uz || '-'}</td>
                    <td>{category?.name?.uz || '-'}</td>
                    <td>
                      <span className={`status-badge ${statusMeta.tone}`}>{statusMeta.label}</span>
                    </td>
                    <td>
                      <button className="action-btn" onClick={() => openModal(p)} aria-label="Edit product">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button className="action-btn delete" onClick={() => confirmDelete(p)} aria-label="Delete product">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="pagination-info">
          <div>Showing {showingFrom} to {showingTo} of {total} entries</div>
          <select
            className="limit-select"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Rows per page"
          >
            {[10, 20, 50].map((n) => (
              <option key={n} value={n}>
                {n}/page
              </option>
            ))}
          </select>
        </div>
        <div className="pagination-controls">
          <button disabled={page === 1} onClick={() => handlePageChange(page - 1)} className="page-btn">
            Previous
          </button>
          {[...Array(Math.ceil(total / limit))].map((_, i) => (
            <button
              key={i + 1}
              className={`page-btn ${page === i + 1 ? 'active' : ''}`}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button
            disabled={page >= Math.ceil(total / limit) || total === 0}
            onClick={() => handlePageChange(page + 1)}
            className="page-btn"
          >
            Next
          </button>
        </div>
      </div>

      {deleteModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Delete product confirmation">
          <div className="modal" style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h2>Delete Product</h2>
              <button className="close-btn" onClick={() => setDeleteModalOpen(false)} aria-label="Close">
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)' }}>
                Are you sure you want to delete the product <strong>{productToDelete?.name?.uz}</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setDeleteModalOpen(false)}>
                Cancel
              </button>
              <button className="btn-save" style={{ backgroundColor: 'var(--error)' }} onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteImageModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Delete product image confirmation">
          <div className="modal" style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h2>Delete Image</h2>
              <button className="close-btn" onClick={() => setDeleteImageModalOpen(false)} aria-label="Close">
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)' }}>Are you sure you want to delete this image?</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setDeleteImageModalOpen(false)}>
                Cancel
              </button>
              <button className="btn-save" style={{ backgroundColor: 'var(--error)' }} onClick={handleDeleteImage}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Product editor">
          <div className="modal">
            <div className="modal-header">
              <h2>{currentProduct ? 'Edit Product' : 'Add Product'}</h2>
              <button className="close-btn" onClick={() => setModalOpen(false)} aria-label="Close">
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Price</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.price}
                      onChange={(e) => setFormData((p) => ({ ...p, price: e.target.value }))}
                      min="0"
                      step="1"
                    />
                  </div>

                  <div className="form-group">
                    <label>Discount</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.discount}
                      onChange={(e) => setFormData((p) => ({ ...p, discount: e.target.value }))}
                      min="0"
                      step="1"
                    />
                  </div>

                  <div className="form-group">
                    <label>Percent</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.percent}
                      onChange={(e) => setFormData((p) => ({ ...p, percent: e.target.value }))}
                      min="0"
                      max="100"
                      step="1"
                    />
                  </div>

                  <div className="form-group">
                    <label>Status</label>
                    <select
                      className="form-control"
                      value={formData.status}
                      onChange={(e) => setFormData((p) => ({ ...p, status: Number(e.target.value) }))}
                    >
                      {statusOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.is_popular}
                        onChange={(e) => setFormData((p) => ({ ...p, is_popular: e.target.checked }))}
                        style={{ marginRight: '8px' }}
                      />
                      Popular
                    </label>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.on_sale}
                        onChange={(e) => setFormData((p) => ({ ...p, on_sale: e.target.checked }))}
                        style={{ marginRight: '8px' }}
                      />
                      On sale
                    </label>
                  </div>

                  <div className="form-group">
                    <label>Brand</label>
                    <select
                      className="form-control"
                      value={formData.brand_guid}
                      onChange={(e) => setFormData((p) => ({ ...p, brand_guid: e.target.value }))}
                    >
                      <option value="">Select brand (optional)</option>
                      {brands.map((b) => (
                        <option key={b.guid} value={b.guid}>
                          {b?.name?.uz || b.guid}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Category</label>
                    <select
                      className="form-control"
                      value={formData.category_guid}
                      onChange={(e) => setFormData((p) => ({ ...p, category_guid: e.target.value }))}
                    >
                      <option value="">Select category (optional)</option>
                      {categories.map((c) => (
                        <option key={c.guid} value={c.guid}>
                          {c?.name?.uz || c.guid}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="lang-tabs">
                  {['uz', 'ru', 'oz'].map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      className={`lang-tab ${activeTab === lang ? 'active' : ''}`}
                      onClick={() => setActiveTab(lang)}
                    >
                      {lang.toUpperCase()}
                    </button>
                  ))}
                </div>

                <div className="form-group">
                  <label>Name ({activeTab.toUpperCase()})</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name[activeTab]}
                    onChange={(e) => handleInputChange('name', activeTab, e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description ({activeTab.toUpperCase()})</label>
                  <ReactQuill
                    key={activeTab}
                    ref={quillRef}
                    theme="snow"
                    value={formData.description[activeTab] || ''}
                    onChange={(content, delta, source) => {
                      if (source === 'user') {
                        handleInputChange('description', activeTab, content);
                      }
                    }}
                    modules={modules}
                    className="quill-editor"
                  />
                </div>

                <div className="product-images">
                  <div className="images-header">
                    <h3>Images</h3>
                    <div className="images-actions">
                      <input
                        type="number"
                        className="form-control"
                        value={imageType}
                        onChange={(e) => setImageType(Number(e.target.value) || 1)}
                        min="1"
                        step="1"
                        aria-label="Image type"
                        style={{ width: '120px' }}
                      />
                      <button
                        type="button"
                        className="btn-secondary"
                        disabled={!currentProduct?.guid || imageUploading}
                        onClick={() => imageInputRef.current && imageInputRef.current.click()}
                      >
                        {imageUploading ? 'Uploading...' : 'Upload Image'}
                      </button>
                      <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleImageFileChange}
                      />
                    </div>
                  </div>

                  {!currentProduct?.guid ? (
                    <p className="images-hint">Save the product first to manage images.</p>
                  ) : imagesLoading ? (
                    <p className="images-hint">Loading images...</p>
                  ) : productImages.length === 0 ? (
                    <p className="images-hint">No images yet.</p>
                  ) : (
                    <div className="images-grid">
                      {productImages.map((img) => {
                        const meta = getStatusMeta(img.status);
                        return (
                          <div key={img.guid} className="image-card">
                            <div className="image-preview">
                              <img src={img.url} alt="Product" loading="lazy" />
                            </div>
                            <div className="image-meta">
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <span className={`status-badge ${meta.tone}`}>{meta.label}</span>
                                <span className="status-badge muted">Type: {img.image_type ?? 1}</span>
                              </div>
                              <div className="image-buttons">
                                <button
                                  type="button"
                                  className="mini-btn"
                                  onClick={() => toggleImageStatus(img)}
                                  disabled={Number(img.status) === PRODUCT_STATUS.DELETED}
                                >
                                  {Number(img.status) === PRODUCT_STATUS.ACTIVE ? 'Deactivate' : 'Activate'}
                                </button>
                                <button type="button" className="mini-btn danger" onClick={() => confirmDeleteImage(img)}>
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* inline hint removed; status shown in footer for visibility */}
              </div>
              <div className="modal-footer">
                <div className="save-status" aria-live="polite">
                  {saveStatus ? (
                    <span className={`save-status-text ${saveStatus.type}`}>{saveStatus.message}</span>
                  ) : null}
                </div>
                <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>
                  Close
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
